import { InfoIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import useQuoteResultCosts from 'components/hooks/quotes/useQuoteResultCosts'
import CostBreakdown from 'components/quote_explorer/CostBreakdown'
import { type SuggestedBenchmark } from 'components/quoting/types'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { ButtonLink, Tooltip } from 'slab'
import { capitalize } from 'utils'
import { useGetQuotingQuoteResultsQuoteIdResults as useGetQuoteResults } from 'utils/web_api'

type Props = {
  quoteId: string
  quoteProcessing: boolean
  isLoading: boolean
  tagText?: string
  period: 'monthly' | 'annual'
  benchmark: SuggestedBenchmark
  nextStepPath: string
  onResultsLoaded: (totalCost: number) => void
  defaultEmployerSpouseContribution: number
  defaultEmployerChildContribution: number
}

const DEFAULT_EMPLOYER_EMPLOYEE_CONTRIBUTION = 100

function SuggestionCard ({
  quoteId,
  quoteProcessing,
  isLoading,
  tagText,
  period,
  benchmark,
  nextStepPath,
  onResultsLoaded,
  defaultEmployerSpouseContribution,
  defaultEmployerChildContribution,
}: Props) {
  const { data: quoteResults, isPending } = useGetQuoteResults(quoteId, {
    metal_tier: benchmark.metal_tier,
    coverage_percentile: parseInt(benchmark.coverage_percentile),
  }, {
    query: {
      enabled: !quoteProcessing,
      refetchOnWindowFocus: false,
    },
  })

  const eligibleEmployees = useMemo(() => {
    return quoteResults?.filter((result) => result.eligible) ?? []
  }, [quoteResults])

  const {
    totalMonthlyEmployeeContribution,
    totalMonthlyEmployerCost,
    currentPlanTotalEmployeeMedicalContribution,
    currentPlanTotalEmployerMedicalContribution,
  } = useQuoteResultCosts(eligibleEmployees, DEFAULT_EMPLOYER_EMPLOYEE_CONTRIBUTION / 100, defaultEmployerSpouseContribution / 100, defaultEmployerChildContribution / 100)

  useEffect(() => {
    if (eligibleEmployees) {
      onResultsLoaded(totalMonthlyEmployeeContribution + totalMonthlyEmployerCost)
    }
  }, [eligibleEmployees, totalMonthlyEmployeeContribution, totalMonthlyEmployerCost])

  const showLoading = isLoading || isPending
  const costMultiplier = period === 'annual' ? 12 : 1

  const customizeLink = () => {
    const searchParams = new URLSearchParams()
    searchParams.append('metal_tier', benchmark.metal_tier)
    searchParams.append('coverage_percentile', benchmark.coverage_percentile)
    searchParams.append('employee_contribution', DEFAULT_EMPLOYER_EMPLOYEE_CONTRIBUTION.toString())
    searchParams.append('spouse_contribution', defaultEmployerSpouseContribution.toString())
    searchParams.append('child_contribution', defaultEmployerChildContribution.toString())
    return `${nextStepPath}?${searchParams.toString()}`
  }

  return (
    <div className='flex flex-col'>
      {tagText && !showLoading && (
        <div className='px-2'>
          <span className='text-xs text-gray-800 bg-gray-100 rounded-t-lg py-1 px-2'>
            {tagText}
          </span>
        </div>
      )}
      <div className='flex flex-col min-w-72 overflow-hidden border border-gray-200 rounded-lg shadow-md group/root has-[.trigger:hover]:shadow-lg transition'>
        <div className='p-4 bg-gray-50'>
          <CostBreakdown
            employeePerPeriodCost={totalMonthlyEmployeeContribution * costMultiplier}
            employeePreviousPerPeriodCost={currentPlanTotalEmployeeMedicalContribution * costMultiplier}
            employerPerPeriodCost={totalMonthlyEmployerCost * costMultiplier}
            employerPreviousPerPeriodCost={currentPlanTotalEmployerMedicalContribution * costMultiplier}
            isLoading={showLoading}
            shouldDisplayComparisons
            period={period}
          />
        </div>
        <div className='flex flex-col gap-4 p-4'>
          <div className='flex flex-col gap-2'>
            <p className='text-xs text-gray-500'>
              Assumed dependent coverage
            </p>
            {showLoading
              ? (
                <SkeletonItem heightClass='h-8' widthClass='w-3/4' />
                )
              : (
                <div className='flex flex-col gap-1'>
                  <p className='text-sm text-gray-800'>
                    {defaultEmployerSpouseContribution}% for 1st dependent
                  </p>
                  <p className='text-sm text-gray-800'>
                    {defaultEmployerChildContribution}% for additional dependents
                  </p>
                </div>
                )}
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-1'>
              <p className='text-xs text-gray-500'>
                Benchmark level
              </p>
              <Tooltip
                content='The benchmark defines the level of coverage you want to base your allowance amounts on.'
              >
                <InfoIcon className='w-4 h-4 text-gray-500' />
              </Tooltip>
            </div>
            {showLoading
              ? (
                <SkeletonItem widthClass='w-24' />
                )
              : (
                <p className='text-sm text-gray-800'>
                  {capitalize(benchmark.metal_tier)} {benchmark.coverage_percentile}
                </p>
                )}
          </div>
        </div>
        <div className='flex p-4 border-t border-gray-200'>
          <ButtonLink
            variant='secondary'
            width='full'
            disabled={showLoading}
            href={customizeLink()}
          >
            Customize
          </ButtonLink>
        </div>
      </div>
    </div>
  )
}

export default SuggestionCard
